<script setup>
import { computed, watch } from 'vue';
import { Head, useForm, usePage } from '@inertiajs/vue3';
import { getActivePinia } from 'pinia';

import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import SaturnLogo from '@/Components/SaturnLogo.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import SaturnButton from '@/Components/Buttons/SaturnButton.vue';
import SimpleStarBackground from '@/Components/SimpleStarBackground.vue';
import TextInput from '@/Components/TextInput.vue';

const page = usePage();

const form = useForm({
    identifier: '',
    sms_consent: false,
});

// Reset all active stores to initial state
getActivePinia()._s.forEach((store) => store.$reset());

const otpSmsAvailable = computed(() => Boolean(page.props.otpSmsAvailable));
const mobileRedirectUri = computed(() => page.props.mobileRedirectUri ?? null);
const mobileState = computed(() => page.props.mobileState ?? '');
// When in mobile flow, "Sign in with password" should go to /login with callback params so password login redirects to saturn://
// Always use route('login') (with optional params) so WebView/Inertia gets the password page directly; avoid /loginlegacy 301 (SAT-817).
// SAT-887: include method=password so LoginController skips the /oauth/authorize → /loginotp redirect.
const signInWithPasswordUrl = computed(() => {
    const base = route('login');
    const params = new URLSearchParams({ method: 'password' });
    if (mobileRedirectUri.value) {
        params.set('redirect_uri', mobileRedirectUri.value);
        if (mobileState.value) params.set('state', mobileState.value);
    }
    return base + '?' + params.toString();
});

/** True when identifier looks like an email (contains @). */
const identifierIsEmail = computed(() => (form.identifier || '').trim().includes('@'));

/** TCPA checkbox only after Send when server says this phone user has not opted in yet (streamlined first view). */
const smsConsentBlockVisible = computed(
    () =>
        otpSmsAvailable.value &&
        !identifierIsEmail.value &&
        !!form.identifier?.trim() &&
        Boolean(form.errors.sms_consent)
);
const canSubmit = computed(() => {
    if (smsConsentBlockVisible.value && !form.sms_consent) return false;
    return true;
});

watch(
    () => form.identifier,
    () => {
        if (form.errors.sms_consent && typeof form.clearErrors === 'function') {
            form.clearErrors('sms_consent');
        }
        form.sms_consent = false;
    }
);

const submit = () => {
    form.post(route('loginotp.send'));
};

// Safe full-page navigation (works in webviews where window may be undefined)
const navigateTo = (url) => {
    const target = typeof window !== 'undefined' ? window : globalThis;
    if (target?.location) target.location.href = url;
};
</script>

<template>

    <Head :title="$t('Login')" />
    <div class="fixed inset-0 -z-50">
        <SimpleStarBackground />
    </div>
    <AuthenticationCard>
        <template #logo>
            <SaturnLogo height="32" />
        </template>

        <div class="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {{ otpSmsAvailable ? $t('Login otp invited identifier hint') : $t('Enter your email address to receive a verification code.') }}
        </div>

        <form @submit.prevent="submit">
            <div>
                <InputLabel for="identifier" :value="otpSmsAvailable ? $t('Email or Phone') : $t('Email')" />
                <TextInput id="identifier" dusk="loginotp-identifier" v-model="form.identifier" :type="otpSmsAvailable ? 'text' : 'email'" class="mt-2 block w-full" required autofocus autocomplete="username" :placeholder="otpSmsAvailable ? $t('Placeholder email or phone') : $t('Placeholder email')" />
                <InputError class="mt-2" :message="form.errors.identifier" />

                <!-- Vonage SMS opt-in: shown after Send returns sms_consent error for users without prior opt-in (invitees, etc.). -->
                <div v-if="smsConsentBlockVisible" class="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <label class="flex cursor-pointer items-start gap-3 text-left">
                        <input v-model="form.sms_consent" type="checkbox" class="mt-1 h-4 w-4 rounded border-gray-300 text-lunar focus:ring-lunar" :class="{ 'border-red-500': form.errors.sms_consent }" />
                        <span class="text-xs text-gray-700">
                            {{ $t('SMS consent disclosure') }}
                            <a href="https://saturntime.com/privacy-policy" target="_blank" rel="noopener noreferrer" class="underline text-gray-900 hover:text-gray-700">{{ $t('Privacy Policy') }}</a>.
                        </span>
                    </label>
                    <InputError v-if="form.errors.sms_consent" class="mt-2" :message="form.errors.sms_consent" />
                    <p v-if="form.errors.sms_consent" class="mt-2 text-xs text-gray-700">
                        {{ $t('Login otp sms consent hint') }}
                    </p>
                </div>
            </div>

            <div class="lg:flex lg:justify-between lg:items-center lg:mt-6">
                <div class="mt-6 lg:mt-0">
                    <SaturnButton duskAttribute="loginotp-sendCodeButton" :disabled="form.processing || !canSubmit" :fullWidth="true" @saturn-button-clicked="submit">
                        {{ $t('Send Code') }}
                    </SaturnButton>
                </div>

                <div class="lg:order-first mt-4 lg:mt-0">
                    <!-- Native link so WebView follows href; no JS so app doesn't intercept (Sign in with password). -->
                    <a dusk="loginotp-signInWithPassword" :href="signInWithPasswordUrl" target="_top" class="cursor-pointer font-medium underline">
                        {{ $t('Sign in with password') }}
                    </a>
                </div>
            </div>

            <div class="border-t border-saturn-gray font-medium mt-6 pt-6 space-y-2">
                <p class="text-sm text-gray-600 dark:text-gray-400 font-normal">
                    {{ $t('Login otp invited signup hint') }}
                </p>
                <span>
                    {{ $t('New to Saturn?') }}
                    <a :href="route('register')" target="_top" dusk="loginotp-signupButton" class="cursor-pointer underline" @click.prevent="navigateTo(route('register'))">{{ $t('Sign up') }}</a>
                </span>
            </div>
        </form>
    </AuthenticationCard>
</template>
